import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaBolt,
  FaPause,
  FaPlay,
  FaRedoAlt,
  FaRocket,
  FaTrophy,
  FaVolumeMute,
  FaVolumeUp,
} from "react-icons/fa";
import GameRoomBackButton from "../GameRoomBackButton";
import { submitSoloGameResult } from "../../../services/games/gameService";
import {
  TABLE_HEIGHT,
  TABLE_WIDTH,
  addMultiball,
  createPinballWorld,
  renderPinballWorld,
  resetPinballWorld,
  servePinball,
  stepPinballWorld,
} from "./pinballEngine";
import { createPinballAudio } from "./pinballAudio";
import "./Pinball.css";

const HIGH_SCORE_KEY = "hobbli-pinball-orbital-vault-high-score-v1";
const SOUND_KEY = "hobbli-pinball-sound-v1";
const BALLS_PER_GAME = 3;
const BALL_SAVE_MS = 8000;

const MISSIONS = [
  {
    key: "comet-run",
    title: "COMET RUN",
    description: "Shoot either orbit 4 times",
    eventTypes: ["orbit-left", "orbit-right"],
    goal: 4,
    seconds: 28,
  },
  {
    key: "vault-break",
    title: "VAULT BREAK",
    description: "Hit the vault bumpers 10 times",
    eventTypes: ["bumper"],
    goal: 10,
    seconds: 32,
  },
  {
    key: "reactor-overload",
    title: "REACTOR OVERLOAD",
    description: "Strike the reactor 3 times",
    eventTypes: ["reactor"],
    goal: 3,
    seconds: 25,
  },
];

function loadHighScore() {
  try {
    const value = Number(window.localStorage.getItem(HIGH_SCORE_KEY));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function loadSoundSetting() {
  try {
    return window.localStorage.getItem(SOUND_KEY) !== "off";
  } catch {
    return true;
  }
}

function formatScore(value) {
  return Number(value || 0).toLocaleString();
}

function makeGameState(highScore) {
  return {
    status: "ready", // ready | playing | paused | between | over
    score: 0,
    highScore,
    ball: 1,
    multiplier: 1,
    extraBalls: 0,
    nextExtraMission: 2,
    missionsCompleted: 0,
    jackpots: 0,
    multiballs: 0,
    maxMultiplier: 1,
    hoblliLanes: Array(6).fill(false),
    missionTargets: Array(3).fill(false),
    warpTargets: Array(3).fill(false),
    missionReady: false,
    multiballReady: false,
    multiball: false,
    mission: null,
    lastMissionKey: null,
    message: "Press Start to enter the Orbital Vault.",
    startedAt: 0,
    ballSaveUntil: 0,
    reported: false,
  };
}

export default function Pinball({ currentUser }) {
  const canvasRef = useRef(null);
  const worldRef = useRef(createPinballWorld());
  const gameRef = useRef(makeGameState(loadHighScore()));
  const controlsRef = useRef({ left: false, right: false, plunger: false });
  const audioRef = useRef(null);
  const frameRef = useRef(null);
  const lastFrameRef = useRef(null);
  const mountedRef = useRef(true);
  const serveTimerRef = useRef(null);
  const hudPublishAtRef = useRef(0);
  const currentUserRef = useRef(currentUser);

  const [hud, setHud] = useState(() => ({ ...gameRef.current }));
  const [soundEnabled, setSoundEnabled] = useState(loadSoundSetting);

  currentUserRef.current = currentUser;

  const publishHud = useCallback((force = false) => {
    const now = performance.now();
    if (!force && now - hudPublishAtRef.current < 120) return;
    hudPublishAtRef.current = now;
    const game = gameRef.current;
    setHud({
      ...game,
      hoblliLanes: [...game.hoblliLanes],
      missionTargets: [...game.missionTargets],
      warpTargets: [...game.warpTargets],
      mission: game.mission ? { ...game.mission } : null,
    });
  }, []);

  const playSound = useCallback((name) => {
    audioRef.current?.play(name);
  }, []);

  const setMessage = useCallback(
    (message, force = false) => {
      gameRef.current.message = message;
      publishHud(force);
    },
    [publishHud]
  );

  const addScore = useCallback((basePoints, label = null) => {
    const game = gameRef.current;
    const earned = Math.round(basePoints * game.multiplier);
    game.score += earned;
    if (game.score > game.highScore) game.highScore = game.score;
    if (label) game.message = `${label} +${formatScore(earned)}`;
    return earned;
  }, []);

  const saveLocalHighScore = useCallback((score) => {
    try {
      const existing = loadHighScore();
      window.localStorage.setItem(HIGH_SCORE_KEY, String(Math.max(existing, score)));
    } catch {
      // Local storage is optional; gameplay remains fully functional without it.
    }
  }, []);

  const reportGame = useCallback(async () => {
    const game = gameRef.current;
    if (game.reported || !game.startedAt) return;
    game.reported = true;
    if (!currentUserRef.current?.id) return;

    try {
      await submitSoloGameResult({
        gameKey: "pinball",
        result: "completed",
        score: Math.max(0, Math.round(game.score)),
        durationMs: Math.max(250, Date.now() - game.startedAt),
        stats: {
          table: "orbital-vault",
          missions_completed: game.missionsCompleted,
          multiballs: game.multiballs,
          jackpots: game.jackpots,
          max_multiplier: game.maxMultiplier,
        },
      });
    } catch (error) {
      // Score persistence should never be able to stop a local game.
      console.warn("Pinball score submission failed:", error);
    }
  }, []);

  const finishGame = useCallback(() => {
    const game = gameRef.current;
    if (game.status === "over") return;
    game.status = "over";
    game.multiball = false;
    game.mission = null;
    game.message = `GAME OVER · ${formatScore(game.score)} POINTS`;
    saveLocalHighScore(game.score);
    playSound("drain");
    publishHud(true);
    reportGame();
  }, [playSound, publishHud, reportGame, saveLocalHighScore]);

  const scheduleServe = useCallback(
    (delay = 700, message = "Ball ready. Hold Launch, then release.") => {
      if (serveTimerRef.current) window.clearTimeout(serveTimerRef.current);
      gameRef.current.status = "between";
      publishHud(true);
      serveTimerRef.current = window.setTimeout(() => {
        if (!mountedRef.current || gameRef.current.status === "over") return;
        servePinball(worldRef.current);
        gameRef.current.status = "playing";
        gameRef.current.ballSaveUntil = 0;
        gameRef.current.message = message;
        publishHud(true);
      }, delay);
    },
    [publishHud]
  );

  const handleEmptyDrain = useCallback(() => {
    const game = gameRef.current;
    const now = Date.now();

    if (game.ballSaveUntil && now < game.ballSaveUntil) {
      game.ballSaveUntil = 0;
      game.message = "BALL SAVED · Back to the launcher!";
      playSound("extra");
      scheduleServe(520, "BALL SAVED · Hold Launch and fire again.");
      return;
    }

    addScore(2500, "END-OF-BALL BONUS");

    if (game.extraBalls > 0) {
      game.extraBalls -= 1;
      game.message = "EXTRA BALL · Shoot again!";
      playSound("extra");
      scheduleServe(850, "EXTRA BALL · Hold Launch and release.");
      return;
    }

    if (game.ball < BALLS_PER_GAME) {
      game.ball += 1;
      game.multiplier = Math.max(1, game.multiplier - 1);
      game.multiball = false;
      game.mission = null;
      game.message = `BALL ${game.ball} · Prepare to launch.`;
      scheduleServe(900, `BALL ${game.ball} · Hold Launch and release.`);
      return;
    }

    finishGame();
  }, [addScore, finishGame, playSound, scheduleServe]);

  const completeMission = useCallback(() => {
    const game = gameRef.current;
    if (!game.mission) return;
    const title = game.mission.title;
    game.missionsCompleted += 1;
    game.lastMissionKey = game.mission.key;
    game.mission = null;
    addScore(50000, `${title} COMPLETE`);
    playSound("mission");

    if (game.missionsCompleted >= game.nextExtraMission) {
      game.extraBalls += 1;
      game.nextExtraMission += 3;
      addScore(10000, "EXTRA BALL LIT");
      game.message = "EXTRA BALL AWARDED · Mission streak bonus!";
      playSound("extra");
    }
  }, [addScore, playSound]);

  const startMission = useCallback(() => {
    const game = gameRef.current;
    const choices = MISSIONS.filter((mission) => mission.key !== game.lastMissionKey);
    const definition = choices[Math.floor(Math.random() * choices.length)] || MISSIONS[0];
    game.missionReady = false;
    game.missionTargets = Array(3).fill(false);
    game.mission = {
      ...definition,
      progress: 0,
      timeLeft: definition.seconds,
    };
    game.message = `${definition.title} · ${definition.description}`;
    playSound("mission");
  }, [playSound]);

  const advanceMission = useCallback(
    (eventType) => {
      const game = gameRef.current;
      const mission = game.mission;
      if (!mission || !mission.eventTypes.includes(eventType)) return;
      mission.progress += 1;
      addScore(2500, `${mission.title} ${mission.progress}/${mission.goal}`);
      if (mission.progress >= mission.goal) completeMission();
    },
    [addScore, completeMission]
  );

  const processEvents = useCallback(
    (events) => {
      if (!events.length) return;
      const game = gameRef.current;
      const world = worldRef.current;
      let drained = 0;

      events.forEach((event) => {
        switch (event.type) {
          case "launch":
            game.ballSaveUntil = Date.now() + BALL_SAVE_MS;
            game.message = `BALL ${game.ball} IN PLAY · 8 second ball save`;
            playSound("launch");
            break;

          case "flipper":
            // Mechanical flipper sound is triggered on control press; no score here.
            break;

          case "bumper":
            addScore(1000, "VAULT BUMPER");
            playSound("bumper");
            advanceMission("bumper");
            break;

          case "sling":
            addScore(300, "SLINGSHOT");
            playSound("sling");
            break;

          case "kickback":
            addScore(500, `${event.side === "left" ? "LEFT" : "RIGHT"} KICKBACK`);
            game.message = `${event.side === "left" ? "LEFT" : "RIGHT"} KICKBACK SAVE!`;
            playSound("sling");
            break;

          case "unstick":
            // Physics-only safeguard: no points, no message spam.
            break;

          case "post":
            addScore(75);
            break;

          case "center-save":
            addScore(500, "CENTER SAVE");
            playSound("sling");
            break;

          case "mission-target": {
            if (!game.missionTargets[event.id]) {
              game.missionTargets[event.id] = true;
              addScore(1750, `MISSION TARGET ${event.id + 1}`);
            } else {
              addScore(450, "MISSION TARGET");
            }
            playSound("target");
            if (!game.missionReady && game.missionTargets.every(Boolean)) {
              game.missionReady = true;
              addScore(7500, "MISSION READY");
              game.message = "MISSION READY · Hit the Reactor to begin.";
              playSound("mission");
            }
            break;
          }

          case "warp-target": {
            if (!game.warpTargets[event.id]) {
              game.warpTargets[event.id] = true;
              addScore(2200, `WARP TARGET ${event.id + 1}`);
            } else {
              addScore(500, "WARP TARGET");
            }
            playSound("target");
            if (!game.multiballReady && game.warpTargets.every(Boolean)) {
              game.multiballReady = true;
              addScore(10000, "WARP LOCK READY");
              game.message = "MULTIBALL READY · Hit the Reactor!";
              playSound("mission");
            }
            break;
          }

          case "lane": {
            const index = event.index ?? 0;
            if (!game.hoblliLanes[index]) {
              game.hoblliLanes[index] = true;
              addScore(1250, `HOBBLI LANE ${index + 1}`);
              playSound("lane");
            } else {
              addScore(300);
            }
            if (game.hoblliLanes.every(Boolean)) {
              game.hoblliLanes = Array(6).fill(false);
              game.multiplier = Math.min(5, game.multiplier + 1);
              game.maxMultiplier = Math.max(game.maxMultiplier, game.multiplier);
              addScore(12000, `MULTIPLIER ${game.multiplier}X`);
              game.message = `HOBBLI COMPLETE · Scoring is now ${game.multiplier}X!`;
              playSound("mission");
            }
            break;
          }

          case "orbit-left":
          case "orbit-right":
            addScore(3000, event.type === "orbit-left" ? "COMET ORBIT" : "NOVA ORBIT");
            playSound("lane");
            advanceMission(event.type);
            break;

          case "ramp":
            addScore(4500, "VAULT RAMP");
            playSound("lane");
            break;

          case "reactor":
            playSound("reactor");
            if (game.multiball) {
              game.jackpots += 1;
              addScore(100000, "JACKPOT");
              game.message = `JACKPOT! ${formatScore(100000 * game.multiplier)} POINTS`;
              playSound("jackpot");
              advanceMission("reactor");
            } else if (game.multiballReady) {
              game.multiballReady = false;
              game.warpTargets = Array(3).fill(false);
              game.multiball = true;
              game.multiballs += 1;
              addScore(25000, "ORBITAL MULTIBALL");
              addMultiball(world, 2);
              game.message = "ORBITAL MULTIBALL · Reactor shots score JACKPOTS!";
              playSound("jackpot");
            } else if (game.missionReady && !game.mission) {
              addScore(5000, "MISSION START");
              startMission();
            } else {
              addScore(6000, "REACTOR");
              advanceMission("reactor");
            }
            break;

          case "drain":
            drained += 1;
            break;

          default:
            break;
        }
      });

      if (drained > 0) {
        playSound("drain");
        if (world.balls.length > 0) {
          if (game.multiball && world.balls.length === 1) {
            game.multiball = false;
            game.message = "MULTIBALL ENDED · Keep the last ball alive.";
          } else {
            game.message = `${world.balls.length} BALL${world.balls.length === 1 ? "" : "S"} STILL IN PLAY`;
          }
        } else {
          game.multiball = false;
          handleEmptyDrain();
        }
      }

      publishHud(true);
    },
    [
      addScore,
      advanceMission,
      handleEmptyDrain,
      playSound,
      publishHud,
      startMission,
    ]
  );

  const startGame = useCallback(() => {
    if (serveTimerRef.current) window.clearTimeout(serveTimerRef.current);
    const highScore = Math.max(gameRef.current.highScore || 0, loadHighScore());
    gameRef.current = makeGameState(highScore);
    const game = gameRef.current;
    game.status = "playing";
    game.startedAt = Date.now();
    game.message = "BALL 1 · Hold Launch, then release.";
    resetPinballWorld(worldRef.current);
    servePinball(worldRef.current);
    controlsRef.current = { left: false, right: false, plunger: false };
    playSound("start");
    publishHud(true);
  }, [playSound, publishHud]);

  const togglePause = useCallback(() => {
    const game = gameRef.current;
    if (game.status === "playing") {
      game.status = "paused";
      controlsRef.current.left = false;
      controlsRef.current.right = false;
      controlsRef.current.plunger = false;
      game.message = "PAUSED · Press P or Resume to continue.";
      publishHud(true);
    } else if (game.status === "paused") {
      game.status = "playing";
      game.message = "ORBITAL VAULT ONLINE";
      lastFrameRef.current = performance.now();
      publishHud(true);
    }
  }, [publishHud]);

  const setControl = useCallback(
    (name, value) => {
      if (gameRef.current.status !== "playing") return;
      if (controlsRef.current[name] === value) return;
      controlsRef.current[name] = value;
      if (value && (name === "left" || name === "right")) playSound("flipper");
      if (value && name === "plunger") playSound("default");
    },
    [playSound]
  );

  useEffect(() => {
    audioRef.current = createPinballAudio();
    audioRef.current.setEnabled(soundEnabled);
    return () => audioRef.current?.dispose();
  }, []);

  useEffect(() => {
    audioRef.current?.setEnabled(soundEnabled);
    try {
      window.localStorage.setItem(SOUND_KEY, soundEnabled ? "on" : "off");
    } catch {
      // optional preference persistence
    }
  }, [soundEnabled]);

  useEffect(() => {
    const keyDown = (event) => {
      const key = event.key;
      if (["ArrowLeft", "ArrowRight", "ArrowDown", " ", "a", "A", "d", "D", "p", "P", "Escape", "Enter"].includes(key)) {
        event.preventDefault();
      }

      if (key === "Enter" && ["ready", "over"].includes(gameRef.current.status)) {
        startGame();
        return;
      }
      if (key === "p" || key === "P" || key === "Escape") {
        togglePause();
        return;
      }
      if (key === "ArrowLeft" || key === "a" || key === "A") setControl("left", true);
      if (key === "ArrowRight" || key === "d" || key === "D") setControl("right", true);
      if (key === "ArrowDown" || key === " ") setControl("plunger", true);
    };

    const keyUp = (event) => {
      const key = event.key;
      if (key === "ArrowLeft" || key === "a" || key === "A") setControl("left", false);
      if (key === "ArrowRight" || key === "d" || key === "D") setControl("right", false);
      if (key === "ArrowDown" || key === " ") setControl("plunger", false);
    };

    const releaseControls = () => {
      controlsRef.current.left = false;
      controlsRef.current.right = false;
      controlsRef.current.plunger = false;
    };

    // A lost keyup (switching focus to DevTools, another window, or tab) must
    // never leave a flipper held in its raised position indefinitely.
    const onVisibilityChange = () => { if (document.hidden) releaseControls(); };
    const onPageHide = () => releaseControls();
    window.addEventListener("keydown", keyDown, { passive: false });
    window.addEventListener("keyup", keyUp);
    window.addEventListener("blur", releaseControls);
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      releaseControls();
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      window.removeEventListener("blur", releaseControls);
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [setControl, startGame, togglePause]);

  useEffect(() => {
    mountedRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    canvas.width = TABLE_WIDTH;
    canvas.height = TABLE_HEIGHT;
    const ctx = canvas.getContext("2d");

    const frame = (time) => {
      if (!mountedRef.current) return;
      const previous = lastFrameRef.current ?? time;
      const dt = Math.min(0.05, Math.max(0, (time - previous) / 1000));
      lastFrameRef.current = time;

      const game = gameRef.current;
      if (game.status === "playing") {
        const events = stepPinballWorld(worldRef.current, dt, controlsRef.current);
        processEvents(events);

        if (game.mission) {
          game.mission.timeLeft = Math.max(0, game.mission.timeLeft - dt);
          if (game.mission.timeLeft <= 0) {
            game.message = `${game.mission.title} FAILED · Relight the mission bank.`;
            game.lastMissionKey = game.mission.key;
            game.mission = null;
            game.missionTargets = Array(3).fill(false);
            game.missionReady = false;
            publishHud(true);
          }
        }
        publishHud(false);
      }

      renderPinballWorld(ctx, worldRef.current, gameRef.current);
      frameRef.current = window.requestAnimationFrame(frame);
    };

    frameRef.current = window.requestAnimationFrame(frame);
    return () => {
      mountedRef.current = false;
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      if (serveTimerRef.current) window.clearTimeout(serveTimerRef.current);
    };
  }, [processEvents, publishHud]);

  const pointerControl = (name, value) => (event) => {
    event.preventDefault();
    if (value && event.currentTarget.setPointerCapture && event.pointerId != null) {
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* no-op */ }
    }
    setControl(name, value);
  };

  const missionCopy = hud.mission
    ? `${hud.mission.title} · ${hud.mission.progress}/${hud.mission.goal} · ${Math.ceil(hud.mission.timeLeft)}s`
    : hud.missionReady
    ? "MISSION READY · HIT REACTOR"
    : "Complete MISSION 1 · 2 · 3";

  const gameButton = hud.status === "over" ? (
    <button className="pinball-primary" onClick={startGame}>
      <FaRedoAlt /> Play Again
    </button>
  ) : hud.status === "ready" ? (
    <button className="pinball-primary" onClick={startGame}>
      <FaPlay /> Start Game
    </button>
  ) : (
    <button className="pinball-secondary" onClick={togglePause} disabled={hud.status === "between"}>
      {hud.status === "paused" ? <FaPlay /> : <FaPause />}
      {hud.status === "paused" ? "Resume" : "Pause"}
    </button>
  );

  return (
    <div className="pinball-page">
      <section className="pinball-shell">
        <header className="pinball-header">
          <GameRoomBackButton />
          <div className="pinball-title">
            <span>HOBBLI PINBALL DIVISION</span>
            <h1>Orbital Vault</h1>
            <p>Light the lanes. Crack the vault. Survive the orbit.</p>
          </div>
          <div className="pinball-header-high">
            <span>High Score</span>
            <strong>{formatScore(hud.highScore)}</strong>
          </div>
        </header>

        <div className="pinball-cabinet">
          <main className="pinball-playfield-frame">
            <div className="pinball-canvas-wrap">
              <canvas
                ref={canvasRef}
                className="pinball-canvas"
                aria-label="Hobbli Orbital Vault pinball table"
              />

              {hud.status === "ready" && (
                <div className="pinball-overlay">
                  <div className="pinball-overlay-card">
                    <span>HOBBLI PINBALL</span>
                    <h2>ORBITAL VAULT</h2>
                    <p>Three balls. Missions, multiplier lanes, multiball, jackpots and extra balls.</p>
                    <button onClick={startGame}><FaPlay /> Start Game</button>
                    <small>Keyboard: A / ← · D / → · Space to launch</small>
                  </div>
                </div>
              )}

              {hud.status === "paused" && (
                <div className="pinball-overlay is-pause">
                  <div className="pinball-overlay-card">
                    <span>TABLE PAUSED</span>
                    <h2>ORBIT SUSPENDED</h2>
                    <button onClick={togglePause}><FaPlay /> Resume</button>
                  </div>
                </div>
              )}

              {hud.status === "over" && (
                <div className="pinball-overlay is-game-over">
                  <div className="pinball-overlay-card">
                    <span>FINAL SCORE</span>
                    <h2>{formatScore(hud.score)}</h2>
                    <p>{hud.score >= hud.highScore ? "Vault record secured." : `High score: ${formatScore(hud.highScore)}`}</p>
                    <button onClick={startGame}><FaRedoAlt /> Play Again</button>
                  </div>
                </div>
              )}
            </div>
          </main>

          <aside className="pinball-console">
            <section className="pinball-dmd pinball-dmd--hero">
              <div className="pinball-dmd-logo">
                <span>HOBBLI</span>
                <strong>ORBITAL<br />VAULT</strong>
                <small>PINBALL // TABLE 01</small>
              </div>
              <div className="pinball-dmd-orbit" aria-hidden="true">
                <i /><i /><b>H</b>
              </div>
            </section>

            <section className="pinball-dmd pinball-score-panel">
              <div>
                <span>SCORE</span>
                <strong>{formatScore(hud.score)}</strong>
              </div>
              <div className="pinball-score-grid">
                <span>BALL <b>{Math.min(hud.ball, BALLS_PER_GAME)}</b></span>
                <span>MULT <b>{hud.multiplier}X</b></span>
                <span>EXTRA <b>{hud.extraBalls}</b></span>
              </div>
            </section>

            <section className="pinball-dmd pinball-message-panel" aria-live="polite">
              <span>TABLE STATUS</span>
              <strong>{hud.message}</strong>
            </section>

            <section className={`pinball-dmd pinball-mission-panel ${hud.mission ? "is-active" : ""}`}>
              <span>MISSION CONTROL</span>
              <strong>{missionCopy}</strong>
              {hud.mission && (
                <div className="pinball-mission-progress">
                  <i style={{ width: `${Math.min(100, (hud.mission.progress / hud.mission.goal) * 100)}%` }} />
                </div>
              )}
              <small>{hud.missionsCompleted} mission{hud.missionsCompleted === 1 ? "" : "s"} completed</small>
            </section>

            <section className="pinball-stats-panel">
              <div><FaTrophy /><span>Jackpots</span><strong>{hud.jackpots}</strong></div>
              <div><FaBolt /><span>Multiballs</span><strong>{hud.multiballs}</strong></div>
              <div><FaRocket /><span>Max Mult</span><strong>{hud.maxMultiplier}X</strong></div>
            </section>

            <section className="pinball-rules-panel">
              <span>VAULT OBJECTIVES</span>
              <ul>
                <li>Spell <b>HOBBLI</b> up top to raise the scoring multiplier.</li>
                <li>Drop all three <b>MISSION</b> targets, then hit the Reactor.</li>
                <li>Drop all three <b>WARP</b> targets, then hit the Reactor for multiball.</li>
                <li>Reactor shots during multiball score <b>JACKPOTS</b>.</li>
                <li>Complete missions to earn extra balls.</li>
              </ul>
            </section>

            <section className="pinball-console-actions">
              {gameButton}
              <button
                className="pinball-secondary"
                onClick={() => setSoundEnabled((value) => !value)}
              >
                {soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
                {soundEnabled ? "Sound On" : "Sound Off"}
              </button>
            </section>

            <section className="pinball-controls-copy">
              <span>DESKTOP CONTROLS</span>
              <p><b>A / ←</b> Left Flipper</p>
              <p><b>D / →</b> Right Flipper</p>
              <p><b>Space / ↓</b> Hold + Release Launcher</p>
              <p><b>P / Esc</b> Pause</p>
            </section>
          </aside>
        </div>

        <div className="pinball-mobile-controls" aria-label="Pinball touch controls">
          <button
            className="pinball-touch pinball-touch--left"
            onPointerDown={pointerControl("left", true)}
            onPointerUp={pointerControl("left", false)}
            onPointerCancel={pointerControl("left", false)}
            onPointerLeave={pointerControl("left", false)}
          >
            <span>◀</span>
            LEFT FLIPPER
          </button>
          <button
            className="pinball-touch pinball-touch--launch"
            onPointerDown={pointerControl("plunger", true)}
            onPointerUp={pointerControl("plunger", false)}
            onPointerCancel={pointerControl("plunger", false)}
            onPointerLeave={pointerControl("plunger", false)}
          >
            <FaRocket />
            HOLD · LAUNCH
          </button>
          <button
            className="pinball-touch pinball-touch--right"
            onPointerDown={pointerControl("right", true)}
            onPointerUp={pointerControl("right", false)}
            onPointerCancel={pointerControl("right", false)}
            onPointerLeave={pointerControl("right", false)}
          >
            <span>▶</span>
            RIGHT FLIPPER
          </button>
        </div>
      </section>
    </div>
  );
}
